// components/Samples/MobileProductSelectVariantsStep.tsx
import { useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { Check } from 'lucide-react'

type ProductVariant = {
  id: number
  name: string
  sku?: string
  gtin?: string
  format?: string
}

type Props = {
  productId: number
  productName: string
  variants: ProductVariant[]
}

export function MobileSelectProductVariantsStep({
  productId,
  productName,
  variants,
}: Props) {
  const { watch, setValue } = useFormContext()
  const lineItems = watch('line_items') ?? []

  const selectedIds: number[] = useMemo(
    () => lineItems.map((li: any) => li.variant_id),
    [lineItems]
  )

  const { selected, notSelected } = useMemo(() => {
    const selected = variants.filter((v) => selectedIds.includes(v.id))
    const notSelected = variants.filter((v) => !selectedIds.includes(v.id))
    return { selected, notSelected }
  }, [variants, selectedIds])

  const [activeTab, setActiveTab] = useState<'selected' | 'not_selected'>(
    'not_selected'
  )

  const toggle = (id: number) => {
    const isSelected = selectedIds.includes(id)

    if (isSelected) {
      const next = lineItems.filter((li: any) => li.variant_id !== id)
      setValue('line_items', next, { shouldValidate: true })
    } else {
      const v = variants.find((vv) => vv.id === id)
      if (!v) return

      const next = [
        ...lineItems,
        {
          product_id: productId,
          product_name: productName,
          variant_id: v.id,
          quantity: 1,
          variant_name: v.name,
          variant_sku: v.sku ?? '',
          variant_format: v.format ?? '',
        },
      ]

      setValue('line_items', next, { shouldValidate: true })
    }
  }

  const renderRow = (v: ProductVariant, checked: boolean) => (
    <button
      key={v.id}
      type="button"
      onClick={() => toggle(v.id)}
      className={`flex w-full items-start gap-3 px-3 py-3 border-b border-gray-200 ${
        checked ? 'bg-emerald-50' : 'bg-white'
      }`}
    >
      <div
        className={`mt-1 flex h-4 w-4 items-center justify-center rounded border ${
          checked ? 'bg-emerald-600 border-emerald-600' : 'border-gray-300'
        }`}
      >
        {checked && <Check className="h-3 w-3 text-white" />}
      </div>
      <div className="flex-1 text-left">
        <div className="text-sm font-medium text-gray-900">{v.name}</div>
        <div className="mt-1 text-xs text-gray-500 space-y-[2px]">
          {v.sku && <div>SKU: {v.sku}</div>}
          {v.gtin && <div>GTIN/UPC: {v.gtin}</div>}
          {v.format && <div>Format/Pack Size: {v.format}</div>}
        </div>
      </div>
    </button>
  )

  const currentList = activeTab === 'selected' ? selected : notSelected
  const selectedCount = selectedIds.length

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="space-y-1 p-2 pb-0">
        <p className="text-xs text-gray-600">
          Product:{' '}
          <span className="font-medium text-fb-green-800">{productName}</span>
        </p>
      </div>

      {/* Tabs */}
      <div className="mt-3 flex border-b border-gray-200 text-sm font-medium">
        <button
          type="button"
          onClick={() => setActiveTab('selected')}
          className={`flex-1 py-2 text-center ${
            activeTab === 'selected'
              ? 'border-b-2 border-emerald-600 text-emerald-700'
              : 'text-gray-500'
          }`}
        >
          Selected ({selected.length})
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('not_selected')}
          className={`flex-1 py-2 text-center ${
            activeTab === 'not_selected'
              ? 'border-b-2 border-emerald-600 text-emerald-700'
              : 'text-gray-500'
          }`}
        >
          Not Selected ({notSelected.length})
        </button>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto bg-gray-50">
        {currentList.length === 0 ? (
          <div className="p-4 text-center text-xs text-gray-500">
            {activeTab === 'selected'
              ? 'No variants selected yet.'
              : 'No more variants to select.'}
          </div>
        ) : (
          currentList.map((v) => renderRow(v, selectedIds.includes(v.id)))
        )}
      </div>

      {/* Summary */}
      {selectedCount > 0 && (
        <div className="px-3 py-2 bg-white border-t border-gray-200">
          <div className="flex justify-center">
            <div className="inline-flex items-center rounded-full bg-emerald-50 px-3 py-1">
              <span className="text-xs font-medium text-emerald-700">
                {selectedCount} variant
                {selectedCount !== 1 ? 's' : ''} selected ({selectedCount} of
                100)
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
